import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { updateCollaborationTasksFilters, fetchCollaborationsTasksThunk } from '../../redux-stores/slices/CollaborationsTasksSlice';
import { AppDispatch, RootState } from '../../redux-stores/store';
import { CollaborationTasksFilters } from '../../types/collaborations/CollaborationTaskTypes';
import { decodeBase64, generateUniqueKey, encodeBase64 } from '../../utils/helpers';
import CollaborationTask from './CollaborationTask';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';

const defaultFiltersValue: CollaborationTasksFilters = {
    page: 1
}

const defaultFiltersKeys = [generateUniqueKey({}), generateUniqueKey({ q: '' })]

const CollaborationTasks: React.FC = () => {
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();

    const queryFilters = searchParams.get('filters');
    const dispatch: AppDispatch = useDispatch();
    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters, defaultFiltersValue) : defaultFiltersValue;
        dispatch(updateCollaborationTasksFilters({ ...decodedFilters, page: 1 }))
    }, [queryFilters])


    const connectionRequired = useSelector((state: RootState) => state.influencer.connectionRequired);

    const filters = useSelector((state: RootState) => state.collaborationTasks.filters);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));

    const collaborationsData = useSelector((state: RootState) => state.collaborationTasks.filtered_lists[filterId!]);
    const collaborations = collaborationsData?.list ?? [];
    const page = collaborationsData?.page ?? 1;
    const has_more = collaborationsData?.has_more ?? true;

    const loading = useSelector((state: RootState) => state.collaborationTasks.loading);
    const error = useSelector((state: RootState) => state.collaborationTasks.error);



    const [searchQuery, setSearchQuery] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);

    const setFilters = (updatedFilters: CollaborationTasksFilters) => {

        const encodedFilters = encodeBase64(updatedFilters);

        setSearchParams({ filters: encodedFilters });
    }

    useEffect(() => {
        if (filters.page && filters.page === 1) {
            setInitialLoading(true);
        }
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(fetchCollaborationsTasksThunk({ filters })).finally(() => {
                setFilterId(generateUniqueKey(filters, ['page']));
                setInitialLoading(false);
            })
        }, 500)

    }, [filters])

    useEffect(() => {
        setSearchQuery(filters.q ?? '');
    }, [filters.q])

    const nextPage = () => {
        if (has_more && !loading) {
            const nextPageNumber = page + 1;
            dispatch(fetchCollaborationsTasksThunk({ filters: { ...filters, page: nextPageNumber } }))
        }
    }

    const searchTask = (q: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (q.trim().length > 3) {
                setFilters({ ...filters, q: q.trim(), page: 1 });
            } else if (q.trim().length === 0) {
                setFilters({ ...filters, q: '', page: 1 });
            }
        }, 500);
        setSearchQuery(q);
    }

    return (
        <div>
            <div className="px-3 mb-3">
                <div className="campaign-layout-search-container position-relative rounded-4">
                    <span>
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z" fill="#BBD8ED" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                    <input type="text" placeholder='Search...' value={searchQuery} onChange={(e) => searchTask(e.target.value)} className="campaign-layout-search-input" />
                    {
                        searchQuery &&
                        <span className='clear-search-icon'> <i className='fa fa-times' onClick={() => searchTask('')}></i> </span>
                    }
                </div>
            </div>
            {
                !defaultFiltersKeys.includes(generateUniqueKey(filters, ['page'])) &&
                <div className="w-100 d-flex align-items-center justify-content-between px-4">
                    <span className='text-muted'>Showing filtered results</span>
                    <button className="fw-bold mb-2" style={{ color: '#0069AD' }} onClick={() => setFilters(defaultFiltersValue)}>Show All</button>
                </div>
            }
            <InfiniteScroll
                dataLength={collaborations.length}
                next={nextPage}
                hasMore={has_more}
                loader={<></>}
                className='list'
            >
                <div className="milestone-cards px-3 gap-3">
                    {
                        !initialLoading && collaborations.map((collaboration, i) => <CollaborationTask key={i} collaborationTask={collaboration} />)
                    }
                    {
                        (initialLoading || loading) && Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={100} borderRadius={20} />)
                    }
                </div>
            </InfiniteScroll>
            {
                !initialLoading && !loading && collaborations.length === 0 &&
                <div className="card h-full card-style rounded-card">
                    <div className="card-body">
                        <div className="h-200px d-flex justify-content-center align-items-center text-center">
                            <div>
                                <h2 className="pop">No collaboration found!</h2>
                                <p className="text-gray-600">Apply on campaigns to get a chance to collaborate with brands</p>
                                <Link className="btn btn-sm rounded-sm btn-primary" to="/">Go to Campaigns</Link>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}

export default CollaborationTasks